/**
 * Module dependencies.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from 'app/components/ui/alert-dialog';
import { Button } from 'app/components/atoms/button/button';
import { Title } from 'app/components/atoms/title/title';
import { CrudForm } from 'app/components/organisms/crud/crud-form';
import { useCrudEdit } from 'app/hooks/requests/crud/use-crud-edit';
import { useDeleteProject } from 'app/hooks/requests/projects/use-delete-project';
import { appRoutes } from 'app/routes';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { createProjectsTemplate } from '../../overview/form-config';
import { useCrudById } from 'app/hooks/requests/crud/use-crud-by-id';
import { omit } from 'lodash';
import { ProjectShares } from './project-shares';

/**
 * Export `ProjectSettings` component.
 */

export function ProjectSettings(): JSX.Element {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const params = useParams();
  const [translate] = useTranslation();
  const template = createProjectsTemplate(translate);
  const { data } = useCrudById(template, { id: params.projectId });
  const { mutate: deleteAction, isLoading } = useDeleteProject();
  const isOwner = !(data as any)?.role || (data as any)?.role === 'owner';
  const isViewer = (data as any)?.role === 'viewer';
  const deleteProject = useCallback(() => {
    try {
      if (!projectId) {
        return;
      }

      deleteAction(projectId);

      navigate(appRoutes.dashboard.projects.base);
    } catch (e) {
      // Nothing to do.
    }
  }, [deleteAction, projectId, navigate]);

  const form = useForm({
    values: data,
    mode: 'all'
  });

  const { mutate: edit } = useCrudEdit(template, { id: params.projectId });
  const onSubmit = async (values: any) => {
    const payload = omit(
      {
        ...data,
        ...values,
        id: params.projectId
      },
      ['userId', 'role', 'isShared']
    );

    // Apply the template's payload normalization (e.g. coercing areaM2 to a
    // number) — this custom submit bypasses CrudEdit's own normalization.
    const normalized = template.edit?.normalizePayload ? await template.edit.normalizePayload(payload) : payload;

    await edit(normalized, {
      onSuccess: () => null
    });
  };

  return (
    <div>
      <Title>{translate('projects.settings.title')}</Title>

      {!isViewer && (
        <CrudForm
          form={form as any}
          formFields={template?.edit?.formFields as any}
          formType={'edit'}
          onSubmit={onSubmit as any}
          submitLabel={translate('common.actions.save')}
        />
      )}

      {isOwner && projectId && (
        <>
          <div style={{ height: 32 }} />
          <ProjectShares projectId={projectId} />
        </>
      )}

      {isOwner && (
        <>
          <div style={{ height: 16 }} />

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button danger onClick={(event: any) => event.stopPropagation()}>
                {translate('projects.settings.delete')}
              </Button>
            </AlertDialogTrigger>

            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{translate('removeAction')}</AlertDialogTitle>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{translate('common.actions.cancel')}</AlertDialogCancel>

                <AlertDialogAction
                  className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
                  disabled={isLoading}
                  onClick={deleteProject}
                >
                  {translate('common.actions.remove')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
